import React from 'react';
import { styled } from '@nykaa/ui-components';
import TextField, { VARIANT } from '@nykaa/ui-components/TextField';
import SearchIcon from '@nykaa/ui-components/Icons/search';
import MicroPhone from 'components/VoiceSearch/MicrophoneV2';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { AB_HP_SEARCH_ANIMATION } from 'constants/experiments';
import PageTypes from 'constants/pageTypes';
import { useIsAddressBarEnabled } from 'hooks/header';

const Wrapper = styled.div`
  padding: ${({ theme: { spacing } }) =>
    `${spacing.spacing0} ${spacing.spacing100}`};
`;

const Container = styled.div`
  display: flex;
  pointer-events: none;
  height: 40px;
`;

const TextFieldWrap = styled.div`
  flex: auto;
  position: relative;
`;

const Wrap = styled.div<{ hasAddressBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  border: ${({ hasAddressBorder }) =>
    hasAddressBorder ? '1px solid rgba(232, 0, 113, 0.24)' : 'none'};
  border-radius: 64px;
  background: ${({ theme }) => theme.colors.snow300};
  svg {
    position: absolute;
    top: 12px;
    left: 16px;
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;
const FixedPart = styled.span`
  color: #69747f;
`;

const DynamicPart = styled.span<{ textColor: string }>`
  color: ${({ textColor }) => (textColor ? textColor : '#69747f')};
`;

const SearchIconWrap = styled(SearchIcon)`
  height: 100%;
  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const Text = styled.div<{ index: number; length: number }>`
  animation: ${({ length }) => `${length * 2000}ms`} ease 0s infinite normal
    forwards running slideInOutAnimation;
  position: absolute;
  top: 100%;
  left: 40px;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  margin-top: 10px;
  color: #69747f;
  animation-delay: ${({ index }) => `${index * 2000}ms`};
`;

interface Props {
  placeHolder: string;
  hasVoiceSearch: boolean;
  searchURL: string;
  handleMicrophone: () => void;
  searchTextHp: [{ text: string; textColor: string }];
  currentPage: string;
}

const SearchBarPlaceholder = ({
  placeHolder,
  hasVoiceSearch,
  searchURL,
  handleMicrophone,
  searchTextHp,
  currentPage,
}: Props) => {
  const searchAnimationEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_HP_SEARCH_ANIMATION, 'A')
  );
  const isAddressBarEnabled = useIsAddressBarEnabled();
  const showSearchAnimation =
    (searchAnimationEnabled &&
      currentPage === PageTypes.HOME &&
      searchTextHp?.length > 0) ||
    false;

  return (
    <Wrapper>
      <a href={searchURL}>
        <Container>
          <TextFieldWrap>
            {!showSearchAnimation && (
              <TextField
                variant={VARIANT.compact}
                icon={<SearchIconWrap size="16px" />}
                placeholder={placeHolder}
                readOnly
                overrides={{
                  Root: {
                    style: {
                      borderRadius: '64px',
                      ...(isAddressBarEnabled && {
                        border: '1px solid rgba(232, 0, 113, 0.24)',
                      }),
                    },
                  },
                }}
              />
            )}
            {showSearchAnimation && (
              <Wrap hasAddressBorder={isAddressBarEnabled}>
                <SearchIcon size="16px" />
                {searchTextHp.map((item, index) => (
                  <Text
                    length={searchTextHp?.length}
                    index={index}
                    key={item.text}
                  >
                    <FixedPart>Search for </FixedPart>
                    <DynamicPart textColor={item.textColor}>
                      {item.text}
                    </DynamicPart>
                  </Text>
                ))}
              </Wrap>
            )}
          </TextFieldWrap>
          {hasVoiceSearch && (
            <MicroPhone
              customClass="microphone"
              handleMicrophone={handleMicrophone}
            />
          )}
        </Container>
      </a>
    </Wrapper>
  );
};

export default SearchBarPlaceholder;
